/**
 * School Edit Form Component
 * Reference implementation following UI Standards
 * Demonstrates pattern for editing existing entities
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox,
  Avatar, Divider
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { SchoolUpdate } from '../../types/school';

// Keep uploaded emblems modest — the renderer scales to ~3 cm wide on the
// cover page, so anything larger is wasted bytes + slow page loads.
const EMBLEM_MAX_BYTES = 2 * 1024 * 1024;
const EMBLEM_ACCEPT = 'image/png,image/jpeg,image/svg+xml';

const SchoolEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<SchoolUpdate>({
    name: '',
    address: '',
    phone: '',
    email: '',
    ieb_centre_number: '',
    reseller_id: null,
    github_folder_structure_id: null,
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // School emblem (logo). The model already carries an ImageField; this form
  // is the only place admins manage it. Three independent slots:
  //   - emblemUrl:   current server-side URL (null when none uploaded)
  //   - emblemFile:  freshly picked file pending upload (null = no change)
  //   - emblemRemove: explicit "clear the emblem" intent (sent as emblem='')
  // emblemFile and emblemRemove are mutually exclusive at submit time.
  const [emblemUrl, setEmblemUrl] = useState<string | null>(null);
  const [emblemFile, setEmblemFile] = useState<File | null>(null);
  const [emblemRemove, setEmblemRemove] = useState(false);
  const [emblemError, setEmblemError] = useState<string | null>(null);
  const emblemInputRef = useRef<HTMLInputElement | null>(null);

  // Object URL for previewing a freshly picked file. Revoked when the file
  // changes or the component unmounts to keep blob URLs from leaking.
  const emblemPreviewUrl = useMemo(
    () => (emblemFile ? URL.createObjectURL(emblemFile) : null),
    [emblemFile]
  );
  useEffect(() => {
    return () => {
      if (emblemPreviewUrl) URL.revokeObjectURL(emblemPreviewUrl);
    };
  }, [emblemPreviewUrl]);

  // Resolved emblem to show in the avatar: live preview > server URL > none
  // (rendered as an initial). emblemRemove suppresses the server URL.
  const displayedEmblem = emblemPreviewUrl
    || (!emblemRemove ? emblemUrl : null);

  const handleEmblemPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    // Reset the input so re-picking the same file fires onChange again.
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setEmblemError('Please pick an image file (PNG, JPG, or SVG).');
      return;
    }
    if (file.size > EMBLEM_MAX_BYTES) {
      setEmblemError(`Image must be under ${Math.round(EMBLEM_MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setEmblemError(null);
    setEmblemFile(file);
    setEmblemRemove(false);
  };

  const handleEmblemRemove = () => {
    setEmblemError(null);
    setEmblemFile(null);
    setEmblemRemove(true);
  };

  // Fetch existing school data on component mount
  useEffect(() => {
    const fetchSchool = async () => {
      if (!id) {
        setError('No school ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/schools/${id}`);
        const school = response.data;

        // Populate form with existing data
        setFormData({
          name: school.name || '',
          address: school.address || '',
          phone: school.phone || '',
          email: school.email || '',
          ieb_centre_number: school.ieb_centre_number || '',
          reseller_id: school.reseller_id || null,
          github_folder_structure_id: school.github_folder_structure_id || null,
          is_active: school.is_active ?? true,
        });
        setEmblemUrl(school.emblem_url || null);
      } catch (err: any) {
        console.error('Error fetching school:', err);
        if (err.response?.status === 404) {
          setError('School not found');
        } else {
          setError('Failed to load school data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchSchool();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'School name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? null : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Build payload with only non-empty optional fields
      const payload: SchoolUpdate = {};

      if (formData.name && formData.name.trim() !== '') payload.name = formData.name;
      if (formData.address && formData.address.trim() !== '') payload.address = formData.address;
      if (formData.phone && formData.phone.trim() !== '') payload.phone = formData.phone;
      if (formData.email && formData.email.trim() !== '') payload.email = formData.email;
      if (formData.ieb_centre_number && formData.ieb_centre_number.trim() !== '') payload.ieb_centre_number = formData.ieb_centre_number;
      if (formData.reseller_id !== null && formData.reseller_id !== undefined) payload.reseller_id = formData.reseller_id;
      if (formData.github_folder_structure_id !== null && formData.github_folder_structure_id !== undefined) payload.github_folder_structure_id = formData.github_folder_structure_id;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;

      // When the emblem is changing (new file picked OR explicit remove),
      // switch to multipart so DRF's ImageField receives the binary. The
      // ModelSerializer with fields='__all__' already accepts emblem; an
      // empty string clears it.
      const emblemChanged = !!emblemFile || emblemRemove;
      if (emblemChanged) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (typeof v === 'boolean') fd.append(k, v ? 'true' : 'false');
          else if (v !== null && v !== undefined) fd.append(k, String(v));
        });
        if (emblemFile) fd.append('emblem', emblemFile);
        else if (emblemRemove) fd.append('emblem', '');
        await axios.patch(`/api/schools/${id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.patch(`/api/schools/${id}`, payload);
      }
      setSuccess(true);

      setTimeout(() => {
        navigate('/schools');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating school:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update school. Please check your input.');
        }
      } else {
        setError('Failed to update school. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching data
  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Show error if data couldn't be loaded
  if (error && !formData.name) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/schools')} sx={{ mt: 2 }}>
          Back to Schools
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/schools')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Schools
        </MuiLink>
        <Typography color="text.primary">Edit School</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit School</Typography>
        <Typography variant="body2" color="textSecondary">Update the school information</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>School updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* School logo / emblem ----------------------------------------
                Drives both the .docx cover page emblem and the in-app school
                badge. Recommended ~200×200 PNG; the renderer scales it down. */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>School logo</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={displayedEmblem || undefined}
                  alt={formData.name || 'School logo'}
                  sx={{ width: 72, height: 72, bgcolor: 'grey.200', fontSize: 28 }}
                >
                  {(formData.name || '?').slice(0, 1).toUpperCase()}
                </Avatar>
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => emblemInputRef.current?.click()}
                      disabled={loading}
                    >
                      {displayedEmblem ? 'Replace logo' : 'Upload logo'}
                    </Button>
                    {displayedEmblem && (
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={handleEmblemRemove}
                        disabled={loading}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="caption" color="textSecondary">
                    PNG, JPG, or SVG. Recommended ~200×200, max{' '}
                    {Math.round(EMBLEM_MAX_BYTES / 1024 / 1024)} MB. Used on cover pages and in-app badges.
                  </Typography>
                  {emblemFile && (
                    <Typography variant="caption" color="primary">
                      New file picked: {emblemFile.name} — save to apply.
                    </Typography>
                  )}
                  {emblemRemove && !emblemFile && (
                    <Typography variant="caption" color="warning.main">
                      Logo will be removed when you save.
                    </Typography>
                  )}
                  {emblemError && (
                    <Typography variant="caption" color="error">
                      {emblemError}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <input
                ref={emblemInputRef}
                type="file"
                accept={EMBLEM_ACCEPT}
                hidden
                onChange={handleEmblemPick}
              />
            </Box>

            <Divider />

            <TextField
              fullWidth label="School Name" name="name"
              value={formData.name || ''} onChange={handleChange}
              error={!!validationErrors.name} helperText={validationErrors.name}
              disabled={loading}
            />

            <TextField
              fullWidth label="Address" name="address"
              value={formData.address || ''} onChange={handleChange}
              disabled={loading}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Phone" name="phone" type="tel"
                value={formData.phone || ''} onChange={handleChange}
                disabled={loading}
              />
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={formData.email || ''} onChange={handleChange}
                disabled={loading}
              />
            </Stack>

            <TextField
              fullWidth label="IEB Centre Number" name="ieb_centre_number"
              value={formData.ieb_centre_number || ''} onChange={handleChange}
              disabled={loading}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth type="number" label="Reseller ID" name="reseller_id"
                value={formData.reseller_id || ''} onChange={handleChange}
                disabled={loading}
              />
              <TextField
                fullWidth type="number" label="GitHub Folder Structure ID" name="github_folder_structure_id"
                value={formData.github_folder_structure_id || ''} onChange={handleChange}
                disabled={loading}
              />
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active ?? true}
                  onChange={handleChange}
                  name="is_active"
                  disabled={loading}
                />
              }
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/schools')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update School'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SchoolEditForm;
